import * as React from "react";
import { Header, SpaceBetween, Spinner, TextContent, Box, Flashbar, FlashbarProps, Icon } from "@amzn/awsui-components-react";
import { useAPI, useAPI2 } from "../../hooks/api-hook";
import {
    EntityFactory,
    EntityPermissionOperationNames,
    EntityType,
    IEntityPermission,
    IMetrics,
    InstanceFactory,
    PageLibraryFactory,
    APIOutput
} from "@amzn/ask-legal-domain";
import { PageSection } from "../../components/page-section/PageSectionComp";
import { PageBaseLayout } from "../../components/page/PageBaseLayout";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import { usePageTitle } from "../../contexts/TitleContext";
import { UIModel } from "../../model/ui-model";
import { FavorButton } from "../../components/common/FavorButton";
import { PageDeprecated } from "../redirects/PageDeprecated";
import { LegalOnlyBadge } from "../../components/common/LegalOnlyTag";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { PageNotFound } from "../redirects/PageNotFound";
import { CaptureAnalyticsComp } from "../../components/analytics/CaptureAnalyticsComp";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { PageContentEvaluation } from "../../components/page/PageContentEvaluation";
import { PageFeedbackModal } from "../../components/page/modal-content/PageFeedbackModal";
import { StarRating } from "../../components/common/StarRating";
import { FeedbackRating, IPageFeedback } from "@amzn/ask-legal-domain";
import "../../styles/component/layout/AskLegalLayout.scss";

export const PageLive = (props: {
    id: string;
    version?: number
}) => {
    const context = React.useContext(AppContext);
    const loadPage2Runner = useAPI(
        context.getPageAPI().load2
    );
    const loadInstanceRunner = useAPI2(
        context.getInstanceAPI().load
    );
    const loadPageHistoryRunner = useAPI(
        context.getPageAPI().loadVersion
    );
    const recordViewHistoryRunner = useAPI(
        context.getCommonAPI().recordViewHistory
    );
    const isFavoritedState = UIModel.State.use<boolean>({
        initialValue: false
    });
    const isAdminRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);
    const isEditorRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);

    const [hasEditorPermission, setHasEditorPermission] = React.useState(false);
    const [hasAdminPermission, setHasAdminPermission] = React.useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
    const [pageRating, setPageRating] = React.useState<FeedbackRating | null>(null);
    const [azaFlashItems, setAzaFlashItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);
    const [azaPublishing, setAzaPublishing] = React.useState(false);

    const submitFeedbackRunner = useAPI(context.getPageFeedbackAPI().submit);
    const loadFeedbackRunner = useAPI(context.getPageFeedbackAPI().load);

    const handleRatingChange = async (newRating: FeedbackRating) => {
        setPageRating(newRating);

        if (loadInstanceRunner.output && loadPage2Runner.status === "Succeeded") {
            const input = IPageFeedback.SubmitFeedbackInput.create({
                pageRef: EntityFactory.toEntityRef(loadPage2Runner.data.output.page),
                rating: newRating,
                sessionId: `session-${Date.now()}`,
                url: window.location.href
            });
            setShowFeedbackModal(true);
        }
    };

    const handlePublishToAza = async () => {
        if (azaPublishing || loadPage2Runner.status !== "Succeeded") return;
        setAzaPublishing(true);
        setAzaFlashItems([]);
        try {
            const page = loadPage2Runner.data.output.page;
            const response = await context.getContentBusAPI().publishPage({
                pageId: props.id,
                instanceId: InstanceFactory.fromEntityId(props.id)
            });
            const output = APIOutput.fromRaw<any>(response.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }
            setAzaFlashItems([{
                type: "success",
                content: `Publish message sent for "${page.title}"`,
                dismissible: true,
                onDismiss: () => setAzaFlashItems([])
            }]);
        } catch (error) {
            setAzaFlashItems([{
                type: "error",
                content: `Failed to publish: ${(error as Error).message}`,
                dismissible: true,
                onDismiss: () => setAzaFlashItems([])
            }]);
        } finally {
            setAzaPublishing(false);
        }
    };

    React.useEffect(() => {
        isAdminRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
            InstanceFactory.fromEntityId(props.id),
            EntityType.Instance,
            EntityPermissionOperationNames.CAN_ADMIN
        ));
    }, []);

    React.useEffect(() => {
        if (isAdminRunner.status === "Succeeded") {
            if (isAdminRunner.output) {
                setHasAdminPermission(true);
                setHasEditorPermission(true);
            } else {
                isEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                    PageLibraryFactory.fromEntityId(props.id),
                    EntityType.PageLibrary,
                    EntityPermissionOperationNames.CAN_EDIT
                ));
            }
        }
    }, [isAdminRunner.status]);

    React.useEffect(() => {
        if (isEditorRunner.status === "Succeeded" && isEditorRunner.output) {
            setHasEditorPermission(true);
        }
    }, [isEditorRunner.status]);

    const pageTitle = React.useMemo(() => {
        if (!props.version && loadPage2Runner.status === "Succeeded") {
            return { title: loadPage2Runner.data.output.page.title, withSuffix: false };
        }
        if (props.version && loadPageHistoryRunner.status === "Succeeded") {
            return {
                title: `${loadPageHistoryRunner.data.output.title} (Version ${props.version})`,
                withSuffix: true
            };
        }
        return { title: undefined, withSuffix: false };
    }, [props.version, loadPage2Runner.status, loadPage2Runner.data, loadPageHistoryRunner.status, loadPageHistoryRunner.data]);

    usePageTitle(pageTitle.title, pageTitle.withSuffix);

    React.useEffect(() => {
        if (loadPage2Runner.status === "Succeeded") {
            isFavoritedState.setValue(loadPage2Runner.data.output.isFavorited);
            recordViewHistoryRunner.submitRun(
                IMetrics.RecordViewHistoryInput.create({
                    url: `/page/${props.id}/live`,
                    entityId: props.id,
                    type: "LivePage"
                })
            );
            loadExistingFeedback();
        }
    }, [loadPage2Runner.status]);

    const loadExistingFeedback = async () => {
        if (loadPage2Runner.status === "Succeeded") {
            try {
                const identity = await context.getIdentity();
                const input = IPageFeedback.LoadFeedbackInput.create({
                    pageRef: EntityFactory.toEntityRef(loadPage2Runner.data.output.page),
                    userId: identity.id // Load feedback for current user only
                });

                await loadFeedbackRunner.submitRun(input);
            } catch (error) {
                console.error("Failed to load existing feedback:", error);
            }
        }
    };

    React.useEffect(() => {
        if (loadFeedbackRunner.status === "Succeeded" && loadFeedbackRunner.data.output.feedbacks.length > 0) {
            const userFeedback = loadFeedbackRunner.data.output.feedbacks[0];
            setPageRating(userFeedback.rating);
        }
    }, [loadFeedbackRunner.status]);

    React.useEffect(() => {
        if (props.version === null || props.version === undefined) {
            loadPage2Runner.submitRun(
                props.id
            );
        } else {
            loadPageHistoryRunner.submitRun(
                EntityFactory.fromEntityAttributes(
                    props.id,
                    EntityType.Page,
                    props.version
                )
            );
        }
        loadInstanceRunner.invoke(InstanceFactory.fromEntityId(props.id));
    }, [props.id, props.version]);

    if (!props.version) {
        // Render live page
        return (
            <React.Fragment>
                <React.Fragment>
                    {loadPage2Runner.status === "Succeeded" && !loadPage2Runner.data.output.page.deprecated && (
                        <div className="sherpa-page-header">
                            <SpaceBetween direction="vertical" size="xxxs">
                                <div className="sherpa-page-title">
                                    <h2>{loadPage2Runner.data.output.page.title}</h2>
                                    <FavorButton
                                        size="big"
                                        isFavoritedState={isFavoritedState}
                                        targetEntityRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                                    />
                                    <LegalOnlyBadge pageLibraryId={PageLibraryFactory.fromEntityId(props.id)}/>
                                </div>
                                <TextContent className="sherpa-page-title-date">
                                    <small>
                                        &nbsp;&nbsp;&nbsp;&nbsp;Updated on {
                                            DateTimeFormatter.toSimpleDate(
                                                loadPage2Runner.data.output.page.freshnessDate ?
                                                    loadPage2Runner.data.output.page.freshnessDate :
                                                    loadPage2Runner.data.output.page.lastModifiedDate,
                                                1
                                            )
                                        }
                                        {!!loadInstanceRunner.output?.preferences?.features?.page?.showLastModifiedBy &&
                                            (
                                                <>
                                                    &nbsp;by <a
                                                        style={{ textDecoration: "None" }}
                                                        href={`https://phonetool.amazon.com/users/${loadPage2Runner.data.output.page.lastModifiedBy.id}`}
                                                        target="_blank"
                                                    >
                                                        {loadPage2Runner.data.output.page.lastModifiedBy.name}
                                                    </a>
                                                </>
                                            )}
                                        {context.hasLab(Preference.Lab.DevMode) && hasAdminPermission && (
                                            <>
                                                &nbsp;&middot;&nbsp;
                                                <a
                                                    style={{ textDecoration: "none", cursor: azaPublishing ? "wait" : "pointer" }}
                                                    onClick={handlePublishToAza}
                                                >
                                                    <Icon name="upload" size="small" />&nbsp;Publish to AZA
                                                </a>
                                            </>
                                        )}
                                    </small>
                                </TextContent>
                                {azaFlashItems.length > 0 && <Flashbar items={azaFlashItems} />}
                                {context.hasLab(Preference.Lab.PageFeedback) &&
                                    <Box margin={{ top: "xs" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
                                            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                                                How useful was this page:
                                            </span>
                                            <StarRating
                                                value={pageRating}
                                                onChange={handleRatingChange}
                                                size="medium"
                                            />
                                            {loadPage2Runner.status === "Succeeded" && loadInstanceRunner.output && (
                                                <PageFeedbackModal
                                                    pageId={props.id}
                                                    instanceId={loadInstanceRunner.output.id}
                                                    visible={showFeedbackModal}
                                                    onDismiss={() => setShowFeedbackModal(false)}
                                                    pageRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                                                    selectedRating={pageRating}
                                                />
                                            )}
                                        </div>
                                    </Box>
                                }
                                <Box margin={{ top: "s" }}>
                                    <PageContentEvaluation
                                        pageRef={{
                                            id: props.id,
                                            version: props.version || 0,
                                            type: EntityType.Page
                                        }}
                                    />
                                </Box>
                            </SpaceBetween>
                        </div>
                    )}
                    {loadPage2Runner.status === "Running" &&
                        <span
                            className="awsui-util-status-inactive vertical-center horizontal-center"
                            style={{ paddingTop: "25px" }}
                        >
                            <Spinner size="large"/> Locating Page Resources...
                        </span>
                    }
                </React.Fragment>
                <React.Fragment>
                    {loadPage2Runner.status === "Error" && (
                        loadPage2Runner.data.err.code === 403 ?
                            <PermissionDenied entityId={props.id} accessType="LivePage"/> : <PageNotFound/>
                    )}
                    {loadPage2Runner.status === "Succeeded" && <>
                        {loadPage2Runner.data.output.page.deprecated ?
                            <PageDeprecated
                                pageOwner={loadPage2Runner.data.output.page.pageOwner}
                                forwardingUrl={loadPage2Runner.data.output.page.forwardingUrl}
                            /> :
                            <CaptureAnalyticsComp
                                entityRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                            >
                                <PageBaseLayout expandable
                                    mainContent={
                                        <PageSection.View
                                            section={loadPage2Runner.data.output.page.sections.find(s => s.location === "Main")}
                                            editor={hasEditorPermission}
                                        />
                                    }
                                    rightContent={
                                        <PageSection.View
                                            section={loadPage2Runner.data.output.page.sections.find(s => s.location === "Right")}
                                            editor={hasEditorPermission}
                                        />
                                    }
                                    hideRightSection={
                                        !!loadPage2Runner.data.output.page.sections.find(s => s.location === "Right") &&
                                        loadPage2Runner.data.output.page.sections.find(s => s.location === "Right").containers.length === 0
                                    }
                                />
                            </CaptureAnalyticsComp>
                        }
                    </>}
                </React.Fragment>
            </React.Fragment>
        );
    }
    // Render history page for specific version
    return (
        <React.Fragment>
            <React.Fragment>
                {loadPageHistoryRunner.status === "Succeeded" ? (
                    <Header variant="h2">{loadPageHistoryRunner.data.output.title}</Header>
                ) : (
                    <Header variant="h2">{props.id.split("-")[2]}</Header>
                )}
            </React.Fragment>
            <React.Fragment>
                {loadPageHistoryRunner.status === "Running" && <Spinner />}
                {loadPageHistoryRunner.status === "Error" && (
                    loadPageHistoryRunner.data.err.code === 403 ?
                        <PermissionDenied entityId={props.id} accessType="LivePage"/> : <PageNotFound/>
                )}
                {loadPageHistoryRunner.status === "Succeeded" &&
                    <PageBaseLayout expandable
                        mainContent={
                            <PageSection.View
                                section={loadPageHistoryRunner.data.output.sections.find(s => s.location === "Main")}
                            />
                        }
                        rightContent={
                            <PageSection.View
                                section={loadPageHistoryRunner.data.output.sections.find(s => s.location === "Right")}
                            />
                        }
                    />
                }
            </React.Fragment>
        </React.Fragment>
    );
};
